import { useTranslation } from "react-i18next";
import type { NotificationType } from "@/api/query/useAllNotificationQuery";
import timeAgo from "@/helperFuntions/notificationFormatter";
import {
  getNotificationPresentation,
  isNotificationDetailsTranslationKey,
} from "@/utils/notificationPresentation";

type Props = {
  notification: NotificationType;
};

/** Shared layout for notification bell dropdown rows. */
export function NotificationRowContent({ notification }: Props) {
  const { t } = useTranslation();
  const { Icon, circleClass, iconClass, titleKey } =
    getNotificationPresentation(notification);

  const bodyText = isNotificationDetailsTranslationKey(notification.details)
    ? t(notification.details)
    : notification.details;

  const unread = !notification.isRead;
  const titleClass = unread
    ? "text-[15px] font-bold leading-snug text-gray-900 sm:text-base"
    : "text-[15px] font-semibold leading-snug text-gray-800 sm:text-base";
  const bodyClass = unread
    ? "mt-1 break-words text-[15px] font-semibold leading-relaxed text-gray-800"
    : "mt-1 break-words text-[15px] leading-relaxed text-gray-600";

  return (
    <div className="flex w-full items-start gap-4">
      <div
        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full sm:h-14 sm:w-14 ${circleClass}`}
      >
        <Icon
          className={`h-6 w-6 sm:h-7 sm:w-7 ${iconClass}`}
          aria-hidden
        />
      </div>
      <div className="min-w-0 flex-1 text-left">
        <div className={titleClass}>
          {t(titleKey)}
        </div>
        <div className={bodyClass}>
          {bodyText}
        </div>
        <div className="mt-1.5 text-sm text-gray-400">
          {timeAgo(new Date(notification.createdAt))}
        </div>
      </div>
      {unread ? (
        <div
          className="h-2.5 w-2.5 shrink-0 self-center rounded-full bg-blue-600"
          aria-hidden
        />
      ) : (
        <span className="w-2.5 shrink-0 self-center" aria-hidden />
      )}
    </div>
  );
}
